/**
 * Layout contract for the requirement detail panel (right side of RequirementList).
 *
 * The panel width is user-draggable; these helpers keep the panel inside the
 * container and keep the card content wide enough to stay readable.
 */

export const MIN_LEFT = 280;
export const MIN_RIGHT = 240;
export const MAX_RIGHT = 640;
export const DIVIDER_WIDTH = 6;

/**
 * Card content width (panel width minus padding/border) below which the
 * two-column Descriptions starts stacking single values vertically.
 * Measured on Req_SocM_768: 302px renders「未指定」one character per line,
 * 402px renders it on a single line.
 */
export const MIN_READABLE_CONTENT_WIDTH = 402;

/** paddingLeft 16 on the panel + 2px Card border. */
export const PANEL_HORIZONTAL_CHROME = 18;

/** Default panel width: wide enough for a readable card body. */
export const DEFAULT_RIGHT = MIN_READABLE_CONTENT_WIDTH + PANEL_HORIZONTAL_CHROME;

/** Width available to the Card inside the detail panel. */
export function detailPanelContentWidth(panelWidth: number): number {
  return Math.max(0, panelWidth - PANEL_HORIZONTAL_CHROME);
}

/**
 * Clamp a desired panel width so that the left list keeps MIN_LEFT and the
 * panel stays within [MIN_RIGHT, MAX_RIGHT]. Idempotent.
 */
export function clampRightWidth(preferred: number, containerWidth: number): number {
  const maxWidth = Math.max(
    MIN_RIGHT,
    Math.min(MAX_RIGHT, containerWidth - MIN_LEFT - DIVIDER_WIDTH)
  );
  return Math.min(maxWidth, Math.max(MIN_RIGHT, preferred));
}

/**
 * A hidden tab pane reports a container width of 0. Such a measurement says
 * nothing about the available space and must not be treated as "no room".
 */
export function usableContainerWidth(width: number | null | undefined): number | null {
  return typeof width === 'number' && Number.isFinite(width) && width > 0 ? width : null;
}

/**
 * Rendered panel width for a given user preference.
 *
 * Always derived from the preference, never from the previously rendered
 * width: clamping is not idempotent across container sizes, so feeding the
 * result back in turns one transient 0-width measurement into a permanent
 * MIN_RIGHT panel (「未 定」wrapping one character per line).
 */
export function resolvePanelWidth(
  preferred: number,
  containerWidth: number | null | undefined
): number {
  const usable = usableContainerWidth(containerWidth);
  return usable === null
    ? Math.min(MAX_RIGHT, Math.max(MIN_RIGHT, preferred))
    : clampRightWidth(preferred, usable);
}
